/**
 * Users — the one identity resource.
 *
 * Replaces the identity half of `src/api/userApi.js` and the fourteen keyon
 * roster reads that returned the same rows under fourteen different keys:
 * `Account.GetAccountUsers`, `Account.GetPendingAccountInfo`,
 * `AccountUser.SearchAccountForEdit`, `NursyInfo.GetIndividualAccountNurse`,
 * `Provider.GetAccountProviders`, `ManageNurseList.InternalAddNurse` and the
 * rest — plus their four write twins (`AccountUser.UpdateAccountUser`,
 * `ManageNurseList.InternalUpdateNurse`, `Provider.DeleteAccountUser`,
 * `ManageNurseList.DeleteNurseInternal`).
 *
 * `users` absorbed `account_user`, `nurses` and the staff half of `providers`,
 * so a nurse, a provider and an administrator are one row type discriminated by
 * `account_type` rather than three tables reached by three operations. The
 * role-filtered views the roster screens want are in `staff.js`, built on
 * `listUsers` so the filter vocabulary is stated once.
 */

import api from './client';

// --- the roster ------------------------------------------------------------
//
// `?filter[role]=` takes a comma list of `account_type` values —
// `administrator`, `provider`, `registered_nurse`, `licensed_practical_nurse`,
// `home_health_aid`, `allied_professional`, `geriatric_care_manager`,
// `patient`, `contact`. `?filter[name]=` searches last, first and preferred
// name; `?filter[npi]=`, `?filter[license_number]=` and
// `?filter[certificate_number]=` join `user_licenses`, because a credential is
// a row with a status rather than a string on the person.

export function listUsers(params = {}) {
  return api.list('/users', params);
}

export function getUser(userId, params = {}) {
  return api.get(`/users/${userId}`, params);
}

export function createUser(payload) {
  return api.post('/users', payload);
}

export function updateUser(userId, patch) {
  return api.patch(`/users/${userId}`, patch);
}

/**
 * Soft — the row stays, because audit rows and signatures reference it. Never
 * a patient (409 `patient_account_not_deletable`: the chart would vanish with
 * the account); a patient's portal sign-in is taken away by deactivateUser.
 */
export function deleteUser(userId) {
  return api.delete(`/users/${userId}`);
}

/**
 * Take a person's sign-in away (Trello 0LGomfTd; `users:deactivate`). Staff
 * lose every group membership and allowed override; a patient loses only the
 * portal, and the chart stays. Not yourself, and never the organization's last
 * active administrator (409 `self_account_change_forbidden` /
 * `last_administrator`).
 */
export function deactivateUser(userId, reason) {
  return api.post(`/users/${userId}/deactivate`, reason ? { reason } : {});
}

/**
 * Give a deactivated person their sign-in back (`users:reactivate`). Their
 * memberships are NOT restored: an administrator re-grants what they should
 * hold now.
 */
export function reactivateUser(userId) {
  return api.post(`/users/${userId}/reactivate`);
}

/**
 * Is this NPI / e-mail / username / licence already taken?
 *
 * Signed-in only (Trello fbd6q9UZ: a public answer told anyone whether a
 * person was registered, and patients are users), and hard-throttled: it is
 * still an existence oracle. E-mail and username are answered across every
 * organization, as sign-in needs them unique; a licence or NPI for the
 * caller's organization, where duplicate registration is refused. Pass exactly
 * the identifiers being checked
 * (`npi`, `email`, `username`, `license` + `license_state`, `certificate`,
 * `role`); it replaces the four `Account.Is*Exist` operations, which each
 * answered one of them.
 */
export function checkAvailability(params) {
  return api.get('/users/availability', params);
}

// --- sign-in identity ------------------------------------------------------
//
// Its own scope and its own audit type: seeing that a colleague exists and
// seeing how they sign in are different rights.

export function getUserCredentials(userId) {
  return api.get(`/users/${userId}/credentials`);
}

export function updateUserCredentials(userId, patch) {
  return api.patch(`/users/${userId}/credentials`, patch);
}

/**
 * The name, credential and licence a signature block prints — never the
 * signature image. Replaces `MedicationAdmin.VerifyAccountSignature` and
 * `Directives.GetDirectiveSignerCredential`, which each read a different table
 * and disagreed about what a signer was allowed to assert.
 */
export function getUserSignature(userId) {
  return api.get(`/users/${userId}/signature`);
}

/** Asks pacmny-auth to re-send the verification mail; answers 202, not "sent". */
export function resendVerificationEmail(userId) {
  return api.post(`/users/${userId}/verification-tokens`);
}

export default {
  listUsers, getUser, createUser, updateUser, deleteUser, deactivateUser, reactivateUser, checkAvailability,
  getUserCredentials, updateUserCredentials, getUserSignature,
  resendVerificationEmail,
};
